import { TaskTypeEnum } from '@api';
import { FormStep } from '@components/form';
import { Check, Checkbox, CircleLoader, Dropdown, Icon, LabelWithTooltip, Tooltip } from '@components/ui';
import { Toggle } from '@components/ui/Toggle/Toggle';
import { getMetricIconStatus } from '@data-access';
import { MapperHelper } from '@helpers';
import { NAMESPACES, useTranslation } from '@i18n';
import { riskIndicators as riskIndicatorsMocks } from '@lib';
import { AppRoutes } from '@types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Database } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import React from 'react';
import { useTestSection } from 'src/shared/hooks/questionnaire/useTestSection/useTestSection';

import styles from './TestSection.module.scss';

dayjs.extend(relativeTime);

interface TestSectionProps {
  onBack: () => void;
  onNext: () => void;
  isToggledDetailView: boolean;
  setIsToggledDetailView: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmitting?: boolean;
}

export const TestSection: React.FC<TestSectionProps> = observer(
  ({ isToggledDetailView, onBack, onNext, setIsToggledDetailView, isSubmitting = false }) => {
    const {
      latestAssessment,
      questionnaireData,
      questionnaireAnswers,
      riskMetrics,
      riskIndicators,
      selectedRiskMetrics,
      selectedQuestionnaireAnswersOption,
      loader,
      availableRiskMetrics,
      isDirectSubmit,
      hasAllMetricsSelected,
      hasAnyMetricsSelected,
      getSelectedMetricsPerIndicator,
      getAvailableMetricsPerIndicator,
      handleIndicatorSelect,
      handleMetricSelect,
      handleSelectAll,
      handleQuestionnaireAnswerSelect,
    } = useTestSection();

    const { t } = useTranslation(NAMESPACES.DATASET);
    const handleToggle = () => setIsToggledDetailView(!isToggledDetailView);

    const isConcierge = questionnaireData?.taskType === TaskTypeEnum.concierge;

    if (!isConcierge && (loader || riskMetrics?.length === 0)) {
      return (
        <div className={styles.loader}>
          <CircleLoader id="circle-loader" />
        </div>
      );
    }

    return (
      <div className={styles.section}>
        <FormStep
          title={isConcierge ? 'Assessment summary' : 'Which tests would you like to execute?'}
          totalSteps={2}
          onBack={onBack}
          onNext={onNext}
          disabledNext={!isConcierge && (selectedRiskMetrics.length === 0 || isSubmitting)}
          loadingNext={isSubmitting}
          nextText={isDirectSubmit ? 'Execute' : 'Fill out'}
          hideSteps
          hideModelAndUsecase
        >
          <div>
            <div className={styles.metaBar}>
              <div className={styles.metaCluster}>
                {questionnaireData.useCaseName && (
                  <div className={styles.metaField}>
                    <div className={styles.metaIconTile}>
                      <Icon
                        name="usecase_icon"
                        className={styles.metaTileIcon}
                      />
                    </div>
                    <div>
                      <div className={styles.metaLabel}>Use case</div>
                      <div className={styles.metaValue}>{questionnaireData.useCaseName}</div>
                    </div>
                  </div>
                )}
                {questionnaireData.modelName && (
                  <div className={styles.metaField}>
                    <div className={styles.metaIconTile}>
                      <Icon
                        name="model_icon"
                        className={styles.metaTileIcon}
                      />
                    </div>
                    <div>
                      <div className={styles.metaLabel}>Model</div>
                      <div className={styles.metaValue}>{questionnaireData.modelName}</div>
                    </div>
                  </div>
                )}
                {questionnaireData.dataPipeline?.pipelineName && (
                  <div className={styles.metaField}>
                    <div className={styles.metaIconTile}>
                      <Database
                        size={15}
                        color="#9da7b7"
                        strokeWidth={1.75}
                      />
                    </div>
                    <div>
                      <div className={styles.metaLabel}>{t('dataset:summary.datasetLabel')}</div>
                      <div className={styles.metaValue}>{questionnaireData.dataPipeline.pipelineName}</div>
                    </div>
                  </div>
                )}
              </div>
              {(questionnaireAnswers?.length ?? 0) > 0 && (
                <>
                  <div className={styles.metaDivider} />
                  <div className={styles.metaVersionCol}>
                    <div className={styles.metaVersionLabel}>Assessment version</div>
                    <Dropdown
                      className={styles.dropdown}
                      items={(questionnaireAnswers ?? []).map(questionnaireAnswer => ({
                        label: (
                          <div className={styles.option}>
                            <div className={styles.optionName}>
                              <span>
                                {questionnaireAnswer.assessments[0]?.id
                                  ? `Executed Assessment ID - ${questionnaireAnswer.assessments[0].id}`
                                  : 'Draft Assessment'}
                              </span>
                              {questionnaireAnswer.assessments[0]?.id && (
                                <Link
                                  className={styles.link}
                                  href={`${AppRoutes.assessmentInventory}/${questionnaireAnswer.assessments[0].id}`}
                                  onClick={e => e.stopPropagation()}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <Icon name="open" />
                                </Link>
                              )}
                            </div>
                            <div className={styles.dropdownTime}>({dayjs().to(questionnaireAnswer.timestamp)})</div>
                          </div>
                        ),
                        value: questionnaireAnswer.id,
                      }))}
                      selected={selectedQuestionnaireAnswersOption}
                      includeBlank
                      onSelect={handleQuestionnaireAnswerSelect}
                    />
                    {(questionnaireAnswers?.length ?? 0) > 1 && (
                      <div className={styles.metaVersionHelper}>
                        {questionnaireAnswers.length - 1} earlier execution
                        {questionnaireAnswers.length - 1 !== 1 ? 's' : ''} available
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            {isConcierge && (
              <div className={styles.unavailableMetricsInfo}>
                <Icon
                  name="info"
                  className={styles.infoIcon}
                />
                Metric selection is not available for Concierge assessments. The assessment will be executed directly.
              </div>
            )}
            {!isConcierge && (
              <>
                <div className={styles.detailViewHeader}>
                  <div className={styles.checkboxAll}>
                    <div className={styles.infoLabel}>Select risk indicators which will be assessed:</div>
                    <Checkbox
                      toggleOption={() => {
                        handleSelectAll();
                      }}
                      isChecked={
                        selectedRiskMetrics.length > 0 && selectedRiskMetrics.length === availableRiskMetrics.length
                      }
                      option={{ id: 'selectAll' as string, name: 'Select all metrics' }}
                    />
                  </div>
                  <div className={styles.detailView}>
                    <div className={styles.infoLabel}> Detailed view</div>
                    <Toggle
                      isToggledDetailView={isToggledDetailView}
                      handleToggle={handleToggle}
                    />
                  </div>
                </div>
                {riskMetrics?.some(m => m.availableForAssessment === false) && (
                  <div className={styles.unavailableMetricsInfo}>
                    <Icon
                      name="info"
                      className={styles.infoIcon}
                    />
                    Some metrics are greyed out and cannot be selected. Hover over a disabled metric to see what is
                    missing.
                  </div>
                )}
                {riskIndicators ? (
                  <div className={styles.riskIndicators}>
                    {riskIndicators?.map(indicator => (
                      <div
                        key={indicator.id}
                        className={styles.indicatorCard}
                      >
                        <div
                          key={indicator.name}
                          className={styles.indicator}
                        >
                          <div
                            className={styles.indicatorName}
                            style={{ backgroundColor: riskIndicatorsMocks.find(i => i.name === indicator.name)?.color }}
                            onClick={handleIndicatorSelect.bind(null, indicator?.id)}
                          >
                            {/* {riskIndicatorCard.find(i => i.name === indicator.name)?.icon} */}
                            <span>{MapperHelper.capitalizeString(indicator.name)}</span>
                            <Check
                              className={styles.checkIcon}
                              isChecked={hasAllMetricsSelected(indicator.id)}
                              indeterminate={
                                hasAnyMetricsSelected(indicator.id) && !hasAllMetricsSelected(indicator.id)
                              }
                            />
                          </div>
                          {getAvailableMetricsPerIndicator(indicator.id).length > 0 && (
                            <div className={styles.selectedIndicatorsInfo}>
                              Selected {getSelectedMetricsPerIndicator(indicator.id).length} out of{' '}
                              {getAvailableMetricsPerIndicator(indicator.id).length} metrics
                            </div>
                          )}
                        </div>
                        {isToggledDetailView && (
                          <div className={styles.metrics}>
                            {riskMetrics?.map(metric => {
                              if (metric.indicator.id !== indicator.id) {
                                return null;
                              }

                              const metricRow = (
                                <div
                                  className={clsx(styles.metricsRow, {
                                    [styles.disabled]: !metric.availableForAssessment,
                                  })}
                                  key={metric.id}
                                >
                                  <div
                                    className={styles.metricsHeader}
                                    onClick={handleMetricSelect.bind(null, metric)}
                                  >
                                    <Check
                                      className={styles.checkIcon}
                                      isChecked={selectedRiskMetrics.some(m => m.id === metric?.id)}
                                    />
                                    <LabelWithTooltip
                                      name={metric.name}
                                      description={metric.infoboxDescription}
                                    />
                                  </div>
                                  {metric.availableForAssessment &&
                                    (latestAssessment?.riskMetrics?.find(m => m.name === metric.name)
                                      ? MapperHelper.findStatus(latestAssessment, metric.name)
                                      : getMetricIconStatus({ status: '', placement: 'right' }))}
                                </div>
                              );

                              if (metric.availableForAssessment || !metric.unavailabilityReasons?.length) {
                                return metricRow;
                              }

                              return (
                                <Tooltip
                                  key={metric.id}
                                  content={
                                    <ul>
                                      {metric.unavailabilityReasons.map(reason => (
                                        <li key={reason}>{reason}</li>
                                      ))}
                                    </ul>
                                  }
                                  placement="top"
                                  className={styles.metricUnavailableTooltip}
                                  tooltipClassName={styles.metricUnavailableTooltipTrigger}
                                  isFixed
                                  hideArrow
                                >
                                  {metricRow}
                                </Tooltip>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <CircleLoader className={styles.circleLoader} />
                )}
              </>
            )}
          </div>
        </FormStep>
      </div>
    );
  },
);
